/**
 * Pubblica una pubblicazione e quelle delle sue copie collegate, una dopo l'altra.
 *
 * Non e' un'operazione atomica, e la modale lo dice: ogni obbligo ha la propria
 * pubblicazione, che si pubblica con il proprio comando — versione immutabile,
 * alias, at_map — e una puo' riuscire mentre un'altra no. Per questo l'esito si
 * riporta obbligo per obbligo, e chi legge sa dove riprovare.
 *
 * Avverte anche di una conseguenza meno ovvia: pubblicare un obbligo pubblica
 * **tutto** cio' che vi e' in attesa, non solo la copia. E' la stessa regola del
 * pulsante «Pubblica» di ogni obbligo.
 */

import { useState } from "react";
import { ErroreApi, pubblica } from "../api/client";
import type { VoceDaPubblicare } from "../lib/copie";
import { Modale } from "./Modale";
import { Avviso, Bottone, Chip } from "./primitive";

type Esito = {
  voce: VoceDaPubblicare;
  riuscita: boolean;
  messaggio: string;
  /** Falso quando la pubblicazione e' riuscita ma la at_map non risulta rigenerata. */
  atMap?: boolean;
};

export function ModalePubblicaInTutti({
  voci,
  percorsoDi,
  onChiudi,
  onCompletato,
}: {
  voci: readonly VoceDaPubblicare[];
  percorsoDi: (idNodo: string) => string;
  onChiudi: () => void;
  /** Chiamata alla fine, anche se qualche obbligo non e' riuscito. */
  onCompletato: () => void;
}) {
  const [inCorso, setInCorso] = useState(false);
  const [esiti, setEsiti] = useState<Esito[] | null>(null);

  const pubblicaTutte = async () => {
    setInCorso(true);
    const raccolti: Esito[] = [];
    for (const voce of voci) {
      try {
        const risultato = await pubblica(voce.pubblicazione_id);
        raccolti.push({
          voce,
          riuscita: true,
          messaggio: `versione n. ${risultato.versione.progressivo}`,
          atMap: risultato.at_map_rigenerata,
        });
      } catch (causa) {
        raccolti.push({
          voce,
          riuscita: false,
          messaggio: causa instanceof ErroreApi ? causa.messaggioEsteso : "non riuscita",
        });
      }
      setEsiti([...raccolti]);
    }
    setInCorso(false);
    onCompletato();
  };

  const chiudi = () => {
    if (!inCorso) onChiudi();
  };

  // Una pubblicazione riuscita con la at_map non rigenerata e' un indirizzo
  // che i crawler di ANAC non trovano: lo dice anche «Pubblica» del singolo
  // obbligo, e qui non deve perdersi fra le righe verdi.
  const senzaAtMap = (esiti ?? []).filter((esito) => esito.riuscita && esito.atMap === false);

  return (
    <Modale
      titolo="Pubblica in tutti gli obblighi"
      aperta
      onChiudi={chiudi}
      pedice={
        esiti && !inCorso ? (
          <Bottone variante="primario" onClick={onChiudi}>
            Chiudi
          </Bottone>
        ) : (
          <>
            <Bottone variante="fantasma" onClick={chiudi} disabled={inCorso}>
              Annulla
            </Bottone>
            <Bottone variante="primario" inCorso={inCorso} onClick={() => void pubblicaTutte()}>
              Pubblica in {voci.length} obblighi
            </Bottone>
          </>
        )
      }
    >
      <div className="pila">
        <p>
          Si pubblicano, uno dopo l'altro, gli obblighi che hanno lo stesso documento in bozza.
          Ciascuno riceve la propria versione, con il proprio nome e il proprio indirizzo.
        </p>
        <Avviso tono="attenzione" titolo="Si pubblica tutto cio' che e' in attesa">
          In ciascun obbligo diventa pubblico tutto cio' che e' stato caricato e non ancora
          pubblicato, non solo questo documento: e' la stessa regola del pulsante «Pubblica».
        </Avviso>
        {senzaAtMap.length > 0 && !inCorso ? (
          <Avviso tono="attenzione" titolo="La at_map non risulta rigenerata">
            {senzaAtMap.length === 1
              ? "Dopo una delle pubblicazioni"
              : `Dopo ${senzaAtMap.length} pubblicazioni`}{" "}
            la at_map non risulta rigenerata: verificare lo stato nel pannello di conformita'.
          </Avviso>
        ) : null}
        <ul className="pila-stretta" aria-live="polite">
          {voci.map((voce) => {
            const esito = esiti?.find((riga) => riga.voce.pubblicazione_id === voce.pubblicazione_id);
            return (
              <li key={voce.pubblicazione_id}>
                <span>{percorsoDi(voce.id_nodo)}</span>{" "}
                {esito ? (
                  <Chip
                    tono={
                      !esito.riuscita ? "critico" : esito.atMap === false ? "attenzione" : "positivo"
                    }
                  >
                    {!esito.riuscita
                      ? "non riuscita"
                      : esito.atMap === false
                        ? `pubblicata, ${esito.messaggio}, at_map non rigenerata`
                        : `pubblicata, ${esito.messaggio}`}
                  </Chip>
                ) : null}
                {esito && !esito.riuscita ? (
                  <div className="campo__errore">{esito.messaggio}</div>
                ) : null}
              </li>
            );
          })}
        </ul>
      </div>
    </Modale>
  );
}
